import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Phone, Mail, MapPin, Share2, Landmark, Check, Copy, Globe } from 'lucide-react';
import { condividiOScarica } from '../lib/condivisione';

/**
 * Bigliettino da visita C-LAB con QR vCard — a TUTTO SCHERMO, senza scroll,
 * pensato per fare uno screenshot pulito e inviarlo.
 *
 * Inquadrando il QR con la fotocamera, iOS/Android propongono "Aggiungi
 * contatto" con tutti i campi già compilati. I dati fiscali (P.IVA, SDI, PEC)
 * e bancari (IBAN, BIC) non hanno campi nativi vCard → vanno nel campo NOTE.
 *
 * Copia: telefono/email/indirizzo hanno copia singola; i blocchi fiscale e
 * bancario hanno UN unico pulsante copia che mette negli appunti l'intero blocco.
 */

const CARD = {
  firstName: 'Alessandro',
  lastName:  'Laricchia',
  fullName:  'Alessandro Laricchia',
  title:     'Owner',
  org:       'CLAB SRL',
  tel:       '+393487992387',
  telPretty: '+39 348 799 2387',
  email:     'alessandro.laricchia@me.com',
  url:       'https://www.clabnet.it',
  urlPretty: 'www.clabnet.it',
  street:    'Corso Vittorio Emanuele II 10',
  cap:       '70122',
  city:      'Bari',
  prov:      'BA',
  country:   'Italia',
  piva:      '07906980722',
  cf:        '07906980722',
  sdi:       'M5UXCR1',
  pec:       'clabsrls@pec.it',
  // Dati bancari
  iban:      'LT063250091279044139',
  ibanPretty:'LT06 3250 0912 7904 4139',
  bic:       'REVOLT21',
  bankName:  'Revolut Bank UAB',
};

// vCard 3.0 — il formato più compatibile con le rubriche iOS/Android.
function buildVCard(c) {
  const note =
    `P.IVA ${c.piva} - CF ${c.cf} - SDI ${c.sdi} - PEC ${c.pec} - ` +
    `IBAN ${c.ibanPretty} - BIC ${c.bic} - Banca ${c.bankName}`;
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${c.lastName};${c.firstName};;;`,
    `FN:${c.fullName}`,
    `ORG:${c.org}`,
    `TITLE:${c.title}`,
    `TEL;TYPE=CELL:${c.tel}`,
    `EMAIL;TYPE=INTERNET:${c.email}`,
    `URL:${c.url}`,
    `ADR;TYPE=WORK:;;${c.street};${c.city};${c.prov};${c.cap};${c.country}`,
    `NOTE:${note}`,
    'END:VCARD',
  ].join('\n');
}

export default function BusinessCardModal({ onClose }) {
  const vcard = buildVCard(CARD);
  const [copied, setCopied] = useState(null);

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) {}
  };

  // Condividi il contatto col foglio nativo iOS/Android, o scaricalo dove la
  // condivisione non salverebbe nulla (desktop): vedi lib/condivisione.
  // Solo il file .vcf, mai un URL — iOS ne scaricherebbe l'anteprima.
  const shareCard = () => condividiOScarica(
    new File([vcard], 'alessandro-laricchia-clab.vcf', { type: 'text/vcard' }));

  const adrFull = `${CARD.street}, ${CARD.cap} ${CARD.city} (${CARD.prov})`;

  const contactBlock =
    `${adrFull}, ${CARD.country}\n${CARD.telPretty}\n${CARD.email}`;
  const fiscalBlock =
    `P.IVA ${CARD.piva}\nSDI ${CARD.sdi}\nPEC ${CARD.pec}`;
  const bankBlock =
    `Beneficiario ${CARD.org}\nIBAN ${CARD.ibanPretty}\nBIC/SWIFT ${CARD.bic}`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center md:p-4">
      {/* Card: occupa tutto lo schermo su mobile (h-svh, NIENTE scroll), riquadro su desktop.
          Tutto entra in una schermata → screenshot pulito e condivisibile. */}
      <div className="relative bg-gradient-to-b from-[#0A0E1A] via-[#0A0E1A] to-black text-white w-full h-svh md:h-auto md:max-w-sm md:rounded-3xl overflow-hidden flex flex-col">
        {/* X di chiusura in alto a dx */}
        <button
          onClick={onClose}
          className="absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Contenuto: distribuito per riempire lo schermo senza scroll */}
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] gap-2">

          {/* Logo + QR affiancati (stessa altezza) */}
          <div className="flex items-center justify-center gap-3">
            <img src="/logo-clab.svg?v=6" alt="C-LAB" className="w-24 h-24 rounded-2xl shadow-lg flex-shrink-0" />
            {/* QR bianco su nero: iOS e i lettori moderni leggono i QR invertiti */}
            <div className="bg-[#0A0E1A] p-2 rounded-xl shadow-xl border border-white/15 flex-shrink-0">
              <QRCodeSVG
                value={vcard}
                size={76}
                level="M"
                marginSize={0}
                fgColor="#ffffff"
                bgColor="#0A0E1A"
              />
            </div>
          </div>

          {/* Identità */}
          <div className="text-center">
            <h1 className="text-base font-black tracking-tight leading-tight">{CARD.fullName}</h1>
            <p className="text-xs text-gray-300 leading-tight">{CARD.title} · {CARD.org}</p>
          </div>

          {/* Contatti (indirizzo, telefono, email) — card come le altre, copia unica */}
          <Block title="Contatti" icon={MapPin} onCopy={() => copy(contactBlock, 'cont')} copied={copied === 'cont'}>
            <IconKV icon={MapPin} value={adrFull} />
            <IconKV icon={Phone} value={CARD.telPretty} />
            <IconKV icon={Mail} value={CARD.email} />
          </Block>

          {/* Dati fiscali — UN unico pulsante copia per il blocco */}
          <Block title="Dati fiscali" icon={Landmark} onCopy={() => copy(fiscalBlock, 'fisc')} copied={copied === 'fisc'}>
            <KV label="P.IVA" value={CARD.piva} />
            <KV label="SDI" value={CARD.sdi} />
            <KV label="PEC" value={CARD.pec} />
          </Block>

          {/* Dati bancari — UN unico pulsante copia per il blocco */}
          <Block title="Coordinate bancarie" icon={Landmark} onCopy={() => copy(bankBlock, 'bank')} copied={copied === 'bank'}>
            <KV label="Beneficiario" value={CARD.org} />
            <KV label="IBAN" value={CARD.ibanPretty} full />
            <KV label="BIC/SWIFT" value={CARD.bic} />
          </Block>

          {/* Condividi contatto (foglio nativo iOS/Android) */}
          <button
            onClick={shareCard}
            className="w-full max-w-xs flex items-center justify-center gap-2 bg-white text-gray-900 font-semibold rounded-xl py-1.5 hover:bg-gray-100 transition-colors"
          >
            <Share2 className="w-4 h-4" /> Condividi contatto
          </button>

          {/* Sito web — chiusura del biglietto, centrato in fondo */}
          <a
            href={CARD.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors pt-0.5"
          >
            <Globe className="w-4 h-4" /> {CARD.urlPretty}
          </a>
        </div>
      </div>
    </div>
  );
}

function CopyBtn({ onCopy, copied }) {
  return (
    <button onClick={onCopy} className="p-1 hover:bg-white/10 rounded-xl flex-shrink-0" title="Copia" aria-label="Copia">
      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-400" />}
    </button>
  );
}

// Riga dentro una card: icona + valore. Icona centrata verticalmente rispetto
// al valore (anche quando l'indirizzo va a capo su 2 righe) → allineamento
// coerente con le card Dati fiscali / Coordinate bancarie.
function IconKV({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="flex-1 min-w-0 text-[13px] font-medium text-white leading-tight" title={value}>{value}</span>
    </div>
  );
}

// Blocco con titolo + UN solo pulsante copia (copia tutto il blocco)
function Block({ title, icon: Icon, onCopy, copied, children }) {
  return (
    <div className="w-full max-w-xs bg-white/5 rounded-xl px-3 py-1.5 border border-white/10">
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="flex-1 min-w-0 text-[11px] uppercase tracking-wide text-gray-500 font-semibold truncate" title={title}>{title}</span>
        <CopyBtn onCopy={onCopy} copied={copied} />
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function KV({ label, value, full = false }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-[11px] text-gray-400 w-[70px] flex-shrink-0 whitespace-nowrap truncate" title={label}>{label}</span>
      {/* `full` (IBAN): font più piccolo + niente troncamento → si vede tutto. */}
      <span
        className={`flex-1 min-w-0 font-medium text-white tabular-nums ${full ? 'text-xs whitespace-nowrap' : 'text-[13px] truncate'}`}
        title={value}
      >{value}</span>
    </div>
  );
}
